import { useState, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  DollarSign,
  TrendingUp,
  Eye,
  ShoppingCart,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  Link2,
  Sparkles,
  BarChart3,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { t } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API}/analytics/dashboard`, {
        withCredentials: true,
      });
      setAnalytics(data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-in fade-in duration-500">
        <div className="space-y-2">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80 rounded-2xl" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: t("dashboard.totalEarnings"),
      value: formatCurrency(analytics?.total_earnings),
      icon: DollarSign,
      color: "from-emerald-500 to-emerald-600",
      bgColor: "bg-emerald-500/10",
      textColor: "text-emerald-500",
      change: "+12.5%",
      positive: true,
    },
    {
      title: t("dashboard.todayEarnings"),
      value: formatCurrency(analytics?.today_earnings),
      icon: TrendingUp,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-500/10",
      textColor: "text-blue-500",
      change: "+8.2%",
      positive: true,
    },
    {
      title: t("dashboard.totalSales"),
      value: formatNumber(analytics?.total_sales),
      icon: ShoppingCart,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-500/10",
      textColor: "text-purple-500",
      change: "+23",
      positive: true,
    },
    {
      title: t("dashboard.conversionRate"),
      value: `${analytics?.conversion_rate || 0}%`,
      icon: Percent,
      color: "from-amber-500 to-amber-600",
      bgColor: "bg-amber-500/10",
      textColor: "text-amber-500",
      change: "+2.1%",
      positive: true,
    },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card/95 backdrop-blur-xl border border-border p-4 rounded-xl shadow-xl">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm text-muted-foreground">
              <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }} />
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const hasData = analytics?.total_sales > 0 || analytics?.total_revenue > 0;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("dashboard.title")}</h1>
        <p className="text-muted-foreground mt-1">
          {t("dashboard.subtitle")}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card
            key={index}
            className="group border-border hover:shadow-xl hover:shadow-[#0B1F3A]/5 dark:hover:shadow-white/5 transition-all duration-300 overflow-hidden"
            style={{ animationDelay: `${index * 100}ms` }}
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-4 sm:p-6 relative">
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-8 -translate-y-8">
                <stat.icon className="w-full h-full" />
              </div>
              <div className="flex items-start justify-between relative z-10">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">{stat.title}</p>
                  <p className="text-2xl sm:text-3xl font-bold mt-2 tracking-tight">{stat.value}</p>
                  {hasData && (
                    <div className={`flex items-center gap-1 mt-2 text-sm ${stat.positive ? "text-emerald-500" : "text-red-500"}`}>
                      {stat.positive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                      <span className="font-medium">{stat.change}</span>
                      <span className="text-muted-foreground">{t("dashboard.vsLastWeek")}</span>
                    </div>
                  )}
                </div>
                <div className={`p-3 rounded-xl ${stat.bgColor} group-hover:scale-110 transition-transform duration-300`}>
                  <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-border overflow-hidden" data-testid="earnings-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              {t("dashboard.earnings")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-border/50">
                <span className="text-muted-foreground">{t("dashboard.totalEarnings")}</span>
                <span className="font-bold text-lg">
                  {formatCurrency(analytics?.total_earnings)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border/50">
                <span className="text-muted-foreground">{t("dashboard.monthlyEarnings")}</span>
                <span className="font-semibold">
                  {formatCurrency(analytics?.monthly_earnings)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 bg-emerald-500/5 -mx-6 px-6 rounded-lg">
                <span className="font-semibold">{t("dashboard.todayEarnings")}</span>
                <span className="font-black text-2xl text-emerald-500">
                  {formatCurrency(analytics?.today_earnings)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="stats-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <BarChart3 className="h-5 w-5 text-blue-500" />
              </div>
              {t("dashboard.quickStats")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-border/50">
                <span className="text-muted-foreground">{t("dashboard.totalSales")}</span>
                <span className="font-bold text-lg">
                  {formatNumber(analytics?.total_sales)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border/50">
                <span className="text-muted-foreground">{t("dashboard.totalViews")}</span>
                <span className="font-semibold">
                  {formatNumber(analytics?.total_views)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-muted-foreground">{t("dashboard.conversionRate")}</span>
                <span className="font-semibold">
                  {analytics?.conversion_rate || 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card className="border-border" data-testid="revenue-chart">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="p-2 rounded-lg bg-[#0B1F3A]/10 dark:bg-white/10">
              <TrendingUp className="h-5 w-5 text-[#0B1F3A] dark:text-white" />
            </div>
            {t("dashboard.revenueOverview")}
            <span className="text-sm font-normal text-muted-foreground ml-2">{t("dashboard.last30Days")}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasData ? (
            <div className="h-56 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.chart_data || []}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0B1F3A" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0B1F3A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                  <XAxis
                    dataKey="date"
                    stroke="var(--muted-foreground)"
                    fontSize={12}
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                    }
                  />
                  <YAxis stroke="var(--muted-foreground)" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="earnings"
                    name={t("dashboard.earnings")}
                    stroke="#0B1F3A"
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-80 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground font-medium">{t("dashboard.noRevenueData")}</p>
                <p className="text-sm text-muted-foreground mt-1">{t("dashboard.startSellingAnalytics")}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Best Performing Links */}
        <Card className="border-border" data-testid="best-links">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Link2 className="h-5 w-5 text-purple-500" />
              </div>
              {t("dashboard.bestPerforming")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.best_performing_links?.length > 0 ? (
              <div className="space-y-3">
                {analytics.best_performing_links.slice(0, 5).map((link, index) => (
                  <div
                    key={link.id || index}
                    className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors duration-200 group"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#0B1F3A] to-[#3A6D8C] flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate group-hover:text-primary transition-colors text-sm sm:text-base">{link.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {link.views} {t("dashboard.views")} · {link.sales} {t("dashboard.sales")}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="font-bold text-base sm:text-lg">
                        {formatCurrency(link.price * link.sales, link.currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                  <Link2 className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="font-medium text-muted-foreground">{t("dashboard.noSales")}</p>
                <p className="text-sm text-muted-foreground mt-1 mb-4">{t("dashboard.startSelling")}</p>
                <Link to="/payment-links">
                  <Button variant="outline" size="sm">
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t("dashboard.createPaymentLink")}
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card className="border-border" data-testid="recent-payments">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              {t("dashboard.recentPayments")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.recent_transactions?.length > 0 ? (
              <div className="space-y-3">
                {analytics.recent_transactions.slice(0, 5).map((tx, index) => (
                  <div
                    key={tx.id || index}
                    className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-muted/50"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <ArrowUpRight className="h-5 w-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="font-medium">{formatCurrency(tx.amount, tx.currency)}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(tx.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 text-emerald-500 font-semibold">
                        +{formatCurrency(tx.creator_amount, tx.currency)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="font-medium text-muted-foreground">{t("dashboard.noSales")}</p>
                <p className="text-sm text-muted-foreground mt-1">{t("dashboard.paymentsAppearHere")}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
